/**
 * Data Comparison Utilities
 * Handles deep comparison, type coercion, and field comparison logic
 */

export interface ComparisonResult {
  isIdentical: boolean;
  isDifferent: boolean;
  isMissing: boolean;
  changeType: 'identical' | 'different' | 'missing' | 'new';
}

export interface FieldComparisonResult extends ComparisonResult {
  field: string;
  currentValue: any;
  incomingValue: any;
  isNew: boolean;
}

export interface RecordComparisonResult {
  recordType: 'new' | 'existing' | 'conflict' | 'identical';
  fieldComparisons: FieldComparisonResult[];
  hasConflicts: boolean;
  conflictCount: number;
}

/**
 * Exact deep comparison function - no type coercion, exact matching only
 * Used for comparison view where exact matches should show green
 */
export const exactDeepCompareValues = (val1: any, val2: any): boolean => {
  // Handle null/undefined cases
  if (val1 === val2) return true;
  if (val1 == null || val2 == null) return val1 === val2;
  
  // Handle primitive types - exact comparison only
  if (typeof val1 !== 'object' && typeof val2 !== 'object') {
    // Different types are never equal
    if (typeof val1 !== typeof val2) return false;
    
    // Exact string comparison (case-sensitive, no trimming)
    if (typeof val1 === 'string' && typeof val2 === 'string') {
      return val1 === val2;
    }
    
    // Exact comparison for other primitives
    return val1 === val2;
  }
  
  // Handle arrays
  if (Array.isArray(val1) && Array.isArray(val2)) {
    if (val1.length !== val2.length) return false;
    for (let i = 0; i < val1.length; i++) {
      if (!exactDeepCompareValues(val1[i], val2[i])) return false;
    }
    return true;
  }
  
  // Handle objects
  if (typeof val1 === 'object' && typeof val2 === 'object' && !Array.isArray(val1) && !Array.isArray(val2)) {
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!exactDeepCompareValues(val1[key], val2[key])) return false;
    }
    return true;
  }
  
  return false;
};

/**
 * Robust deep comparison function that handles type differences properly
 * Supports type coercion for number/string/boolean comparisons
 * Use exactDeepCompareValues for exact matching in comparison views
 */
export const deepCompareValues = (val1: any, val2: any): boolean => {
  // Handle null/undefined cases
  if (val1 === val2) return true;
  if (val1 == null || val2 == null) return val1 === val2;
  
  // Handle primitive types with enhanced type coercion
  if (typeof val1 !== 'object' && typeof val2 !== 'object') {
    // Check if both values are numeric (including string numbers)
    if (isValidNumber(val1) && isValidNumber(val2)) {
      return compareNumericValues(val1, val2);
    }
    
    // Handle boolean comparisons
    if (typeof val1 === 'boolean' && typeof val2 === 'string') {
      return val1 === (val2 === 'true' || val2 === '1' || val2 === 'True' || val2 === 'TRUE');
    }
    if (typeof val1 === 'string' && typeof val2 === 'boolean') {
      return (val1 === 'true' || val1 === '1' || val1 === 'True' || val1 === 'TRUE') === val2;
    }
    
    // Handle string comparisons with trimming
    if (typeof val1 === 'string' && typeof val2 === 'string') {
      return val1.trim() === val2.trim();
    }
    
    return val1 === val2;
  }
  
  // Handle objects
  if (typeof val1 === 'object' && typeof val2 === 'object') {
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepCompareValues(val1[key], val2[key])) return false;
    }
    return true;
  }
  
  return false;
};

/**
 * Compare two field values and return detailed comparison result
 */
export const compareFieldValues = (
  field: string,
  currentValue: any,
  incomingValue: any
): FieldComparisonResult => {
  const isIdentical = deepCompareValues(currentValue, incomingValue);
  const isDifferent = currentValue !== undefined && incomingValue !== undefined && !isIdentical;
  const isMissing = currentValue !== undefined && (incomingValue === undefined || incomingValue === null);
  const isNew = currentValue === undefined && incomingValue !== undefined;

  let changeType: 'identical' | 'different' | 'missing' | 'new' = 'identical';
  if (isNew) changeType = 'new';
  else if (isMissing) changeType = 'missing';
  else if (isDifferent) changeType = 'different';

  return {
    field,
    currentValue,
    incomingValue,
    isIdentical,
    isDifferent,
    isMissing,
    isNew,
    changeType
  };
};

/**
 * Compare two complete records and return detailed comparison result
 */
export const compareRecordsDetailed = (current: any, incoming: any): RecordComparisonResult => {
  if (!current && !incoming) {
    return {
      recordType: 'identical',
      fieldComparisons: [],
      hasConflicts: false,
      conflictCount: 0
    };
  }

  if (!current) {
    return {
      recordType: 'new',
      fieldComparisons: [],
      hasConflicts: false,
      conflictCount: 0
    };
  }

  if (!incoming) {
    return {
      recordType: 'existing',
      fieldComparisons: [],
      hasConflicts: false,
      conflictCount: 0
    };
  }

  // Get all unique fields from both objects
  const allFields = new Set<string>();
  Object.keys(current).forEach(key => allFields.add(key));
  Object.keys(incoming).forEach(key => allFields.add(key));

  const fieldComparisons: FieldComparisonResult[] = [];
  let conflictCount = 0;

  allFields.forEach(field => {
    const comparison = compareFieldValues(field, current[field], incoming[field]);
    fieldComparisons.push(comparison);
    
    if (comparison.isDifferent || comparison.isMissing || comparison.isNew) {
      conflictCount++;
    }
  });

  const hasConflicts = conflictCount > 0;
  const recordType: 'new' | 'existing' | 'conflict' | 'identical' = 
    hasConflicts ? 'conflict' : 'identical';

  return {
    recordType,
    fieldComparisons,
    hasConflicts,
    conflictCount
  };
};

/**
 * Get unified field order for side-by-side comparison
 */
export const createUnifiedFieldOrder = (currentObj: any, incomingObj: any): string[] => {
  const allFields = new Set<string>();
  if (currentObj) Object.keys(currentObj).forEach(key => allFields.add(key));
  if (incomingObj) Object.keys(incomingObj).forEach(key => allFields.add(key));
  return Array.from(allFields).sort();
};

/**
 * Convert value to match target type for type-safe operations
 */
export const convertValueToType = (value: any, targetType: string): any => {
  if (value === undefined || value === null) return value;
  
  switch (targetType) {
    case 'number':
      if (typeof value === 'string') {
        const num = Number(value);
        return isNaN(num) ? value : num;
      }
      return value;
      
    case 'string':
      return String(value);
      
    case 'boolean':
      if (typeof value === 'string') {
        return value === 'true' || value === '1';
      }
      if (typeof value === 'number') {
        return Boolean(value);
      }
      return value;
      
    default:
      return value;
  }
};

/**
 * Get the appropriate type for a value based on its content
 */
export const inferValueType = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') {
    // Try to infer if it's a number
    if (!isNaN(Number(value)) && value !== '') return 'number';
    // Try to infer if it's a boolean
    if (value === 'true' || value === 'false') return 'boolean';
    return 'string';
  }
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  return 'unknown';
};

/**
 * Check if two values are semantically equivalent (same value, different types)
 */
export const areValuesSemanticallyEqual = (val1: any, val2: any): boolean => {
  return deepCompareValues(val1, val2);
};

/**
 * Enhanced numeric comparison that handles various number formats
 */
export const compareNumericValues = (val1: any, val2: any): boolean => {
  // Handle null/undefined
  if (val1 == null || val2 == null) return val1 === val2;
  
  // Convert both to numbers
  const num1 = Number(val1);
  const num2 = Number(val2);
  
  // Check if both are valid numbers
  if (isNaN(num1) || isNaN(num2)) {
    return false;
  }
  
  // Compare with epsilon for floating point precision
  const epsilon = 1e-10;
  return Math.abs(num1 - num2) < epsilon;
};

/**
 * Check if a value is a valid number (including string numbers)
 */
export const isValidNumber = (value: any): boolean => {
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && !isNaN(Number(trimmed));
  }
  return false;
};

/**
 * Normalize a value to its most appropriate type for comparison
 */
export const normalizeValueForComparison = (value: any): any => {
  if (value == null) return value;
  
  // If it's already a number, return as is
  if (typeof value === 'number') return value;
  
  // If it's a string, try to convert to number
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return value;
    
    // Try to convert to number
    const num = Number(trimmed);
    if (!isNaN(num)) return num;
    
    // Try to convert to boolean
    if (trimmed.toLowerCase() === 'true') return true;
    if (trimmed.toLowerCase() === 'false') return false;
    
    return trimmed;
  }
  
  return value;
};

/**
 * Get type-safe comparison result for display purposes
 */
export const getTypeSafeComparisonResult = (
  currentValue: any,
  incomingValue: any
): {
  isIdentical: boolean;
  isDifferent: boolean;
  isMissing: boolean;
  isNew: boolean;
  changeType: 'identical' | 'different' | 'missing' | 'new';
  typeInfo: {
    currentType: string;
    incomingType: string;
    typesMatch: boolean;
  };
} => {
  const isIdentical = deepCompareValues(currentValue, incomingValue);
  const isDifferent = currentValue !== undefined && incomingValue !== undefined && !isIdentical;
  const isMissing = currentValue !== undefined && (incomingValue === undefined || incomingValue === null);
  const isNew = currentValue === undefined && incomingValue !== undefined;

  let changeType: 'identical' | 'different' | 'missing' | 'new' = 'identical';
  if (isNew) changeType = 'new';
  else if (isMissing) changeType = 'missing';
  else if (isDifferent) changeType = 'different';

  const currentType = inferValueType(currentValue);
  const incomingType = inferValueType(incomingValue);
  const typesMatch = currentType === incomingType;

  return {
    isIdentical,
    isDifferent,
    isMissing,
    isNew,
    changeType,
    typeInfo: {
      currentType,
      incomingType,
      typesMatch
    }
  };
};

/**
 * Filter fields to show only conflicting ones
 */
export const filterConflictingFields = (
  fieldComparisons: FieldComparisonResult[]
): FieldComparisonResult[] => {
  return fieldComparisons.filter(comparison => 
    comparison.isDifferent || comparison.isMissing || comparison.isNew
  );
};

/**
 * Get summary statistics for a comparison result
 */
export const getComparisonSummary = (comparison: RecordComparisonResult) => {
  const totalFields = comparison.fieldComparisons.length;
  const identicalFields = comparison.fieldComparisons.filter(f => f.isIdentical).length;
  const differentFields = comparison.fieldComparisons.filter(f => f.isDifferent).length;
  const missingFields = comparison.fieldComparisons.filter(f => f.isMissing).length;
  const newFields = comparison.fieldComparisons.filter(f => f.isNew).length;

  return {
    totalFields,
    identicalFields,
    differentFields,
    missingFields,
    newFields,
    conflictCount: comparison.conflictCount,
    hasConflicts: comparison.hasConflicts,
    recordType: comparison.recordType
  };
};
